import { createMemo, createSignal, For, JSX, Show } from 'solid-js'

export type Page = 'home' | 'switch' | 'qr' | 'recorder' | 'templates' | 'entry' | 'manage'


export interface UserCapabilities {
    isManager: boolean
    isOwner: boolean
}

export interface NavItem {
    label: string
    value: Page 
    icon?: () => JSX.Element
    requiredCapabilities?: Partial<UserCapabilities>
    children?: NavItem[]
}

export interface MainNavProps {
    userCapabilities: UserCapabilities
    isOpen: boolean
    root: NavItem
    activeValue?: string
    onNavigate?: (item: NavItem) => void
    logo: JSX.Element
}

const MainNav = (props: MainNavProps) => {
    const [mobileMenuOpen, setMobileMenuOpen] = createSignal(false)

    const canViewItem = (item: NavItem) => {
        if (!item.requiredCapabilities) return true

        const required = item.requiredCapabilities
        if (required.isManager !== undefined && required.isManager !== props.userCapabilities.isManager) {
            return false
        }

        if (required.isOwner !== undefined && required.isOwner !== props.userCapabilities.isOwner) {
            return false
        }

        return true
    }

    const navItems = createMemo(() => {
        const root = props.root
        return (root.children || []).filter(canViewItem)
    })

    const onNavClick = (item: NavItem) => {
        props.onNavigate?.(item)
        setMobileMenuOpen(false)
    }

    const navItemClass = (item: NavItem) => {
        const isActive = props.activeValue === item.value

        return [
            'rounded-md px-3 py-2 text-sm font-medium transition-colors flex items-center justify-start',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/80',
            isActive ? 'bg-purple-500 text-black' : 'text-zinc-200 hover:bg-zinc-700 hover:text-white'
        ].join(' ')
    }

    return (
        <Show when={props.isOpen}>
            <nav class="border-b border-zinc-700 bg-zinc-900/95 px-4 py-2" aria-label="Main navigation">
                <div class="mx-auto flex w-full max-w-4xl items-center justify-between gap-3">
                    <div class="mr-auto">{props.logo}</div>
                    <div class="hidden items-center gap-2 md:flex">
                        <For each={navItems()}>
                            {(item) => (
                                <button
                                    type="button"
                                    class={navItemClass(item)}
                                    onClick={() => onNavClick(item)}
                                >
                                    {item.icon && <span class="mr-2">{item.icon()}</span>}
                                    {item.label}
                                </button>
                            )}
                        </For>
                    </div>

                    <button
                        type="button"
                        class="inline-flex items-center rounded-md border border-zinc-600 px-3 py-2 text-sm font-medium text-zinc-100 hover:bg-zinc-700 md:hidden"
                        onClick={() => setMobileMenuOpen((open) => !open)}
                        aria-expanded={mobileMenuOpen()}
                        aria-controls="main-nav-mobile-menu"
                    >
                        {mobileMenuOpen() ? 'Close' : 'Menu'}
                    </button>
                </div>

                <div
                    id="main-nav-mobile-menu"
                    class={`overflow-hidden transition-all duration-200 ease-out md:hidden ${mobileMenuOpen() ? 'max-h-80 pt-3 opacity-100' : 'max-h-0 pt-0 opacity-0'}`}
                >
                    <div class="mx-auto flex w-full max-w-6xl flex-col gap-2 pb-2">
                        <For each={navItems()}>
                            {(item) => (
                                <button
                                    type="button"
                                    class={`${navItemClass(item)} w-full flex items-center justify-start`}
                                    onClick={() => onNavClick(item)}
                                >
                                    {item.icon && <span class="mr-2">{item.icon()}</span>}
                                    {item.label}
                                </button>
                            )}
                        </For>
                    </div>
                </div>
            </nav>
        </Show>
    )
}

export default MainNav